import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import ColorEditor from '../ColorEditor';

const FooterEditor = ({ content, onContentChange, onArrayFieldChange, onAddArrayItem, onRemoveArrayItem, site, onCreateCustomTheme }) => {
  return (
    <div className="space-y-4">
      {/* Footer Elements */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Footer elements
        </label>
        <div className="space-y-2">
          
          {/* Social Links Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Social links</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="footerSocialToggle"
                type="checkbox" 
                checked={content.showSocialLinks !== false}
                onChange={(e) => onContentChange('showSocialLinks', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="footerSocialToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
          
          {/* Contact Info Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Contact info</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="footerContactToggle"
                type="checkbox" 
                checked={content.showContactInfo !== false}
                onChange={(e) => onContentChange('showContactInfo', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="footerContactToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
          
          {/* Quick Links Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Quick links</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="footerQuickLinksToggle"
                type="checkbox" 
                checked={content.showQuickLinks !== false}
                onChange={(e) => onContentChange('showQuickLinks', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="footerQuickLinksToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
          
          {/* Copyright Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Copyright</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="footerCopyrightToggle"
                type="checkbox" 
                checked={content.showCopyright !== false}
                onChange={(e) => onContentChange('showCopyright', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="footerCopyrightToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
          
          {/* Inverted Colors Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Inverted colors</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="footerInvertedColorsToggle"
                type="checkbox" 
                checked={content.invertedColors || false}
                onChange={(e) => onContentChange('invertedColors', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="footerInvertedColorsToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
        </div>
      </div>


      {/* Social Media Links */}
      {content.showSocialLinks !== false && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Social Media Links</label>
          <div className="space-y-2">
            {(content.socialLinks || []).map((link, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={link.platform || ''}
                  onChange={(e) => onArrayFieldChange('socialLinks', index, { ...link, platform: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Platform (e.g., Facebook)"
                />
                <input
                  type="url"
                  value={link.url || ''}
                  onChange={(e) => onArrayFieldChange('socialLinks', index, { ...link, url: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://..."
                />
                <button
                  onClick={() => onRemoveArrayItem('socialLinks', index)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => onAddArrayItem('socialLinks', { platform: '', url: '' })}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add social link
            </button>
          </div>
        </div>
      )}

      {/* Contact Information */}
      {content.showContactInfo !== false && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Contact Information</label>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={content.email || ''}
                onChange={(e) => onContentChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="contact@business.com"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={content.phone || ''}
                onChange={(e) => onContentChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
              <textarea
                rows={2}
                value={content.address || ''}
                onChange={(e) => onContentChange('address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="123 Business Street, City, State 12345"
              />
            </div>
          </div>
        </div>
      )}

      {/* Quick Links */}
      {content.showQuickLinks !== false && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Quick Links</label>
          <div className="space-y-2">
            {(content.quickLinks || []).map((link, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={link.text || ''}
                  onChange={(e) => onArrayFieldChange('quickLinks', index, { ...link, text: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Link text"
                />
                <input
                  type="text"
                  value={link.url || ''}
                  onChange={(e) => onArrayFieldChange('quickLinks', index, { ...link, url: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="#section or https://..."
                />
                <button
                  onClick={() => onRemoveArrayItem('quickLinks', index)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => onAddArrayItem('quickLinks', { text: '', url: '' })}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add quick link
            </button>
          </div>
        </div>
      )}

      {/* Layout Options */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Layout Options</label>
        <div className="space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Layout Style</label>
            <select
              value={content.layout || 'four-column'}
              onChange={(e) => onContentChange('layout', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="single-column">Single Column</option>
              <option value="two-column">Two Column</option>
              <option value="three-column">Three Column</option>
              <option value="four-column">Four Column</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Text Alignment</label>
            <select
              value={content.textAlign || 'left'}
              onChange={(e) => onContentChange('textAlign', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        </div>
      </div>

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorScheme || 'default'}
        onChange={(value) => onContentChange('colorScheme', value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />

      {/* Copyright Text */}
      {content.showCopyright !== false && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Copyright Text
          </label>
          <input
            type="text"
            value={content.copyrightText || ''}
            onChange={(e) => onContentChange('copyrightText', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="© 2024 Your Company Name. All rights reserved."
          />
        </div>
      )}
    </div>
  );
};

export default FooterEditor;